/**
 * RelationTablePickerButton — JEDEN wspólny silnik „okna wyboru z tabeli" dla KAŻDEGO pola
 * relacji/odwołania do kartoteki systemowej (pola relacji w kartotekach własnych, pola
 * odwołania w kartotekach systemowych). Ten sam wygląd i zachowanie co przy „Dostawcy" na
 * fakturze (`VendorTablePickerButton`): ikona tabeli → `RecordTablePicker` (kolumny z katalogu
 * pól, filtry w nagłówkach) + akcje wiersza „Przejdź do kartoteki" / „Edytuj" oraz „Dodaj
 * nowego" nad tabelą. Edycja i dodawanie otwierają kartę rekordu w oknie NAD tabelą — bez
 * wychodzenia z wypełnianego formularza; nowo dodany rekord od razu trafia do pola.
 *
 * Co kartoteka potrafi, mówi `RELATION_PICKER_ENTITY_ACTIONS` niżej (które okna karty
 * istnieją); adres karty — wspólna mapa `recordCardRoute` (`OpenRecordCardButton`). Kartoteka spoza mapy dostaje samą tabelę wyboru (bez akcji).
 * Akcje są TYLKO w trybie pojedynczego wyboru — tabela wielokrotnego wyboru (relacja
 * „wiele rekordów") ich nie wspiera (zaznaczenie potwierdza się przyciskiem w stopce).
 */

import { type ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { Table } from 'lucide-react';

import { ContractorFormModal } from '@/components/contractors/ContractorFormModal';
import { EmployeeFormModal } from '@/components/employees/EmployeeFormModal';
import { IconButton } from '@/components/IconButton';
import { recordCardRoute } from '@/components/ui/OpenRecordCardButton';
import {
  RecordTablePicker,
  type RecordTablePickerActions,
} from '@/components/ui/RecordTablePicker';
import { UserFormModal } from '@/components/users/UserFormModal';
import { useTabsOptional } from '@/contexts/TabContext';

interface EntityActionsConfig {
  /** Czy kartoteka ma kartę w oknie do edycji (akcja „Edytuj"). */
  canEditInModal: boolean;
  /** Czy kartoteka ma kartę w oknie do tworzenia (akcja „Dodaj nowego"). */
  canCreateInModal: boolean;
  labels: { openCard: string; editRecord: string; createNew: string };
}

/** Klucz = slug adaptera z `SYSTEM_ENTITY_ADAPTERS` (`@/lib/system-entity-adapters`). */
export const RELATION_PICKER_ENTITY_ACTIONS: Record<string, EntityActionsConfig> = {
  contractors: {
    canEditInModal: true,
    canCreateInModal: true,
    labels: {
      openCard: 'tenant.contractors.pickerActions.openCard',
      editRecord: 'tenant.contractors.pickerActions.editSelected',
      createNew: 'tenant.contractors.pickerActions.addNew',
    },
  },
  employees: {
    canEditInModal: true,
    canCreateInModal: true,
    labels: {
      openCard: 'tenant.employees.pickerActions.openCard',
      editRecord: 'tenant.employees.pickerActions.editSelected',
      createNew: 'tenant.employees.pickerActions.addNew',
    },
  },
  users: {
    // Tworzenie/zaproszenie użytkownika zostaje na stronie Użytkownicy (UserFormModal = edycja).
    canEditInModal: true,
    canCreateInModal: false,
    labels: {
      openCard: 'ui.userTablePicker.openCard',
      editRecord: 'ui.userTablePicker.editRecord',
      createNew: 'ui.relationTablePicker.createNew',
    },
  },
  products: {
    canEditInModal: false,
    canCreateInModal: false,
    labels: {
      openCard: 'ui.relationTablePicker.openCard',
      editRecord: 'ui.relationTablePicker.editRecord',
      createNew: 'ui.relationTablePicker.createNew',
    },
  },
  companies: {
    canEditInModal: false,
    canCreateInModal: false,
    labels: {
      openCard: 'ui.relationTablePicker.openCard',
      editRecord: 'ui.relationTablePicker.editRecord',
      createNew: 'ui.relationTablePicker.createNew',
    },
  },
};

/** Zapytania etykiet/list pól relacji — odświeżane po edycji/dodaniu rekordu z okna. */
const RELATION_LABEL_QUERY_PREFIXES = [
  'record-table-picker-list',
  'record-table-picker-prefetch',
  'relation-records',
  'relation-selected',
  'ext-field-reference-label',
  'relation-cell-label',
];

type FormState = { mode: 'create' | 'edit'; recordId?: string } | null;

export interface UseRelationPickerRecordActionsOptions {
  entity: string;
  companyId?: string | null;
  /** Wołany z id nowo dodanego rekordu (po „Dodaj nowego" + zapis). */
  onCreated: (id: string) => void;
  /** Zamyka okno wyboru (przed przejściem na kartę / po dodaniu rekordu). */
  closePicker: () => void;
}

/**
 * Akcje okna wyboru dla kartoteki + okna kart (edycja/dodanie), które trzeba wyrenderować
 * obok `RecordTablePicker`. Używane przez `RelationTablePickerButton` i przez pola, które
 * mają własny przycisk otwierający tabelę (pole odwołania w `ExtFieldValueInput`).
 */
export function useRelationPickerRecordActions({
  entity,
  companyId,
  onCreated,
  closePicker,
}: UseRelationPickerRecordActionsOptions): {
  recordActions: RecordTablePickerActions | undefined;
  modals: ReactNode;
} {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const tabs = useTabsOptional();
  const [formState, setFormState] = useState<FormState>(null);
  const config = RELATION_PICKER_ENTITY_ACTIONS[entity];

  function refreshLabels() {
    for (const key of RELATION_LABEL_QUERY_PREFIXES) {
      void queryClient.invalidateQueries({ queryKey: [key] });
    }
  }

  function handleSaved(id: string) {
    refreshLabels();
    if (formState?.mode === 'create') {
      // Nowy rekord → zamknij kartę i okno wyboru, wstaw rekord do pola.
      setFormState(null);
      closePicker();
      onCreated(id);
    }
    // Edycja: karta zostaje otwarta (user może edytować dalej), zamknie ją X.
  }

  const recordActions: RecordTablePickerActions | undefined = config
    ? {
        // Karta w NOWEJ karcie powłoki — wypełniany formularz zostaje w swojej. Bez powłoki
        // zakładek (podgląd, test) akcji nie ma — mirror `OpenRecordCardButton`.
        onOpenCard: tabs
          ? (row) => {
              const route = recordCardRoute(entity, row.id);
              if (!route) return;
              closePicker();
              tabs.openTab(route);
            }
          : undefined,
        onEditRecord: config.canEditInModal
          ? (row) => setFormState({ mode: 'edit', recordId: row.id })
          : undefined,
        onCreateNew: config.canCreateInModal ? () => setFormState({ mode: 'create' }) : undefined,
        labels: {
          openCard: t(config.labels.openCard),
          editRecord: t(config.labels.editRecord),
          createNew: t(config.labels.createNew),
        },
      }
    : undefined;

  // Zamknięcie karty też odświeża tabelę i etykiety — rekord mógł się zmienić także drogą
  // inną niż „Zapisz" (np. zakładki karty zapisujące się same).
  const close = () => {
    setFormState(null);
    refreshLabels();
  };
  let modals: ReactNode = null;
  if (entity === 'contractors') {
    modals = (
      <ContractorFormModal
        open={formState !== null}
        mode={formState?.mode ?? 'create'}
        contractorId={formState?.recordId}
        companyId={companyId ?? undefined}
        onClose={close}
        onSaved={handleSaved}
      />
    );
  } else if (entity === 'employees') {
    modals = (
      <EmployeeFormModal
        open={formState !== null}
        mode={formState?.mode ?? 'create'}
        employeeId={formState?.recordId}
        companyId={companyId ?? undefined}
        onClose={close}
        onSaved={handleSaved}
      />
    );
  } else if (entity === 'users') {
    modals = (
      <UserFormModal
        open={formState?.mode === 'edit'}
        userId={formState?.recordId}
        onClose={close}
        onSaved={handleSaved}
      />
    );
  }

  return { recordActions, modals };
}

interface RelationTablePickerButtonCommonProps {
  /** Slug kartoteki docelowej (klucz `SYSTEM_ENTITY_ADAPTERS`). */
  entity: string;
  companyId?: string | null;
  disabled?: boolean;
  'data-testid'?: string;
}

export type RelationTablePickerButtonProps = RelationTablePickerButtonCommonProps &
  (
    | { multiple?: false; value: string | null; onChange: (id: string) => void }
    | { multiple: true; value: string[] | null; onChange: (ids: string[]) => void }
  );

/** Ikona tabeli obok pola relacji — otwiera okno wyboru z akcjami (patrz nagłówek pliku). */
export function RelationTablePickerButton(props: RelationTablePickerButtonProps) {
  const { entity, companyId, disabled, 'data-testid': testId } = props;
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  const { recordActions, modals } = useRelationPickerRecordActions({
    entity,
    companyId,
    closePicker: () => setOpen(false),
    onCreated: (id) => {
      if (props.multiple) props.onChange([...(props.value ?? []), id]);
      else props.onChange(id);
    },
  });

  const pickerTestId = testId ? `${testId}-picker` : undefined;

  return (
    <>
      <IconButton
        variant="subtle"
        size="md"
        onClick={() => setOpen(true)}
        disabled={disabled}
        aria-label={t('ui.relationTablePicker.title')}
        title={t('ui.relationTablePicker.title')}
        data-testid={testId}
      >
        <Table />
      </IconButton>
      {props.multiple ? (
        <RecordTablePicker
          entity={entity}
          multiple
          open={open}
          onClose={() => setOpen(false)}
          companyId={companyId ?? undefined}
          value={props.value ?? undefined}
          onChange={(ids) => {
            props.onChange(ids);
            setOpen(false);
          }}
          data-testid={pickerTestId}
        />
      ) : (
        <RecordTablePicker
          entity={entity}
          open={open}
          onClose={() => setOpen(false)}
          companyId={companyId ?? undefined}
          value={props.value ?? undefined}
          onChange={(id) => {
            props.onChange(id);
            setOpen(false);
          }}
          recordActions={recordActions}
          data-testid={pickerTestId}
        />
      )}
      {modals}
    </>
  );
}
